import React, { useState, useEffect, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon,
  InfoOutlined as InfoOutlinedIcon,
  ScienceOutlined as ScienceOutlinedIcon,
  RocketLaunchOutlined as RocketLaunchOutlinedIcon,
  CompareArrowsOutlined as CompareArrowsOutlinedIcon,
} from '@mui/icons-material';
import DiffViewerModal from './DiffViewerModal';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import FileUpload from '../../components/FileUpload/FileUpload';
import LoadingSpinner from '../../components/LoadingSpinner';
import { setSelectedAgentSiteCode, setSelectedAgentName, setSelectedAgentMode } from '../../slices/AppsSlice';
import {
  AGENT_PROFILE_TYPES,
  AGENT_WORKFLOW_TYPES,
  LANGUAGES,
  PUSH_TO_DEV_LABEL,
  TEST_AI_AGENT_LABEL,
  TIMEZONES,
} from '../../constants/Constants';
import useApiCall from '../../hooks/useApiCall';
import useToast from '../../hooks/useToast';
import { TOAST } from '../../constants/ToastMessages';
import { INDIGO, SLATE } from '../../constants/Colors';
import { ACCEPTED_LOGO_FORMATS } from '../../constants/Constants';
import { usePageMeta } from '../../contexts/PageMetaContext';
import BottomActionBar from '../../components/BottomActionBar';
import TrimTextField from '../../components/TrimTextField';

const FIELD_LABEL_SX = {
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: SLATE[700],
  mb: '6px',
  display: 'block',
};

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    fontSize: '0.875rem',
    bgcolor: '#ffffff',
    '& fieldset': { borderColor: SLATE[300] },
    '&:hover fieldset': { borderColor: SLATE[400] },
    '&.Mui-focused fieldset': { borderColor: INDIGO[500] },
  },
  '& .MuiInputBase-input': { color: SLATE[900] },
};

const EMPTY_AGENT_DATA = {
  name: '',
  logo_url: '',
  agent_type: '',
  agent_workflow_type: '',
  business_unit_id: '',
  metadata: {
    timezone: 'Eastern Time (ET)',
    language: 'English',
  },
};

function SectionHeader({ title, description }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: SLATE[900] }}>
        {title}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: '0.8125rem', color: SLATE[500], mt: '2px' }}>
          {description}
        </Typography>
      )}
    </Box>
  );
}

// ── Push to Dev result — plain-language summary for a non-technical audience ──
// (no commit SHAs, PR numbers, or raw backend messages)
const PUSH_DEV_STEP_KEYS = ['commit', 'pull_request', 'merge', 'pipeline'];

const PUSH_DEV_STATUS_BUCKET = {
  success: 'good',
  created: 'good',
  reused: 'good',
  triggered: 'good',
  skipped: 'skipped',
  failed: 'failed',
};

const PUSH_DEV_STEP_TEXT = {
  commit: {
    good: 'Your changes were saved',
    skipped: 'No new changes to save',
    failed: "We couldn't save your changes",
  },
  pull_request: {
    good: 'Submitted for deployment',
    skipped: 'Nothing new to submit',
    failed: "We couldn't submit your changes for deployment",
  },
  merge: {
    good: 'Approved for deployment',
    skipped: 'Approval not needed',
    failed: 'Approval failed',
  },
  pipeline: {
    good: 'Deployment to Dev started',
    skipped: 'Not deployed',
    failed: "Deployment couldn't start",
  },
};

function getPushDevBucket(steps, key) {
  return PUSH_DEV_STATUS_BUCKET[steps?.[key]?.status] || 'skipped';
}

function getPushDevOverall(steps) {
  if (!steps) {
    return {
      tone: 'error',
      title: "Couldn't push to Dev",
      description: 'Something went wrong before we could process your request. Please try again.',
    };
  }

  const commit = getPushDevBucket(steps, 'commit');
  const pullRequest = getPushDevBucket(steps, 'pull_request');
  const merge = getPushDevBucket(steps, 'merge');
  const pipeline = getPushDevBucket(steps, 'pipeline');

  if (pipeline === 'good') {
    return {
      tone: 'success',
      title: 'Pushed to Dev',
      description: "Your agent's latest changes are being deployed to the Dev environment.",
    };
  }
  if (commit === 'skipped' && pullRequest === 'skipped') {
    return {
      tone: 'info',
      title: 'Already up to date',
      description: 'There were no new changes to push — Dev already has the latest version.',
    };
  }
  if (merge === 'failed') {
    return {
      tone: 'error',
      title: 'Approval failed',
      description:
        'Your changes were saved and submitted, but could not be approved automatically — likely a conflicting change. Please contact engineering for help.',
    };
  }
  if (pullRequest === 'failed') {
    return {
      tone: 'error',
      title: "Couldn't submit for deployment",
      description:
        'Your changes were saved, but we could not prepare them for deployment. Please try again or contact engineering.',
    };
  }
  if (commit === 'failed') {
    return {
      tone: 'error',
      title: "Couldn't save changes",
      description: 'We could not save your changes. Please try again.',
    };
  }
  if (pipeline === 'failed') {
    return {
      tone: 'error',
      title: 'Deployment failed to start',
      description: 'Your changes were approved, but deployment could not start. Please contact engineering.',
    };
  }
  return {
    tone: 'info',
    title: 'Push complete',
    description: 'Finished processing your request.',
  };
}

const PUSH_DEV_TONE_ICON = {
  success: <CheckCircleIcon sx={{ fontSize: '2.5rem', color: '#16a34a' }} />,
  error: <CancelIcon sx={{ fontSize: '2.5rem', color: '#dc2626' }} />,
  info: <InfoOutlinedIcon sx={{ fontSize: '2.5rem', color: SLATE[400] }} />,
};

const PUSH_DEV_STEP_ICON = {
  good: <CheckCircleIcon sx={{ fontSize: '1.125rem', color: '#16a34a' }} />,
  failed: <CancelIcon sx={{ fontSize: '1.125rem', color: '#dc2626' }} />,
  skipped: <RemoveCircleOutlineIcon sx={{ fontSize: '1.125rem', color: SLATE[400] }} />,
};

function PushDevStepRow({ stepKey, bucket }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.75 }}>
      {PUSH_DEV_STEP_ICON[bucket]}
      <Typography sx={{ fontSize: '0.8125rem', color: SLATE[700] }}>
        {PUSH_DEV_STEP_TEXT[stepKey][bucket]}
      </Typography>
    </Box>
  );
}

export default function ManageAgent() {
  const { id: agentId } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = !!agentId;
  const isLegacyCreate = !isEditMode && searchParams.get('mode') === 'legacy';

  const tokens = useSelector(state => state?.tokens.value);
  const currentApp = useSelector(state => state?.apps?.selectedApp);
  const { makeRequest } = useApiCall();
  const { success } = useToast();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setPageMeta } = usePageMeta();

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [agentData, setAgentData] = useState(EMPTY_AGENT_DATA);
  const [pushDevDialogOpen, setPushDevDialogOpen] = useState(false);
  const [pushingDev, setPushingDev] = useState(false);
  const [pushDevResult, setPushDevResult] = useState(null);
  const [diffModalOpen, setDiffModalOpen] = useState(false);

  const [agentMode, setAgentMode] = useState(null);
  const [siteCode, setSiteCode] = useState('');

  const [modelData, setModelData] = useState({ default: '', fallback: '' });
  const [availableModels, setAvailableModels] = useState([]);
  const [modelLoaded, setModelLoaded] = useState(false);
  const [modelErrors, setModelErrors] = useState({ default: '', fallback: '' });
  const originalModelRef = useRef({ default: '', fallback: '' });

  const originalDataRef = useRef(EMPTY_AGENT_DATA);
  const agentTypeInputRef = useRef(null);
  const workflowTypeInputRef = useRef(null);
  const businessUnitInputRef = useRef(null);

  const isModelDirty = isEditMode && JSON.stringify(modelData) !== JSON.stringify(originalModelRef.current);
  const isDirty = JSON.stringify(agentData) !== JSON.stringify(originalDataRef.current) || isModelDirty;

  useEffect(() => {
    return () => setPageMeta({});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!currentApp?.id) return;
    makeRequest(`/v1/apps/${currentApp.id}/business_units`, { method: 'GET', authTokens: tokens })
      .then(data => setBusinessUnits(data?.business_units || []))
      .catch(() => {});
  }, [currentApp?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEditMode || !currentApp?.id || !agentId || agentMode !== 'agent') return;
    makeRequest(`/v1/apps/${currentApp.id}/agents/${agentId}/model`, { method: 'GET', authTokens: tokens })
      .then(data => {
        const m = { default: data?.model?.default || '', fallback: data?.model?.fallback || '' };
        setModelData(m);
        originalModelRef.current = m;
        setAvailableModels(data?.available_models || []);
      })
      .catch(() => {})
      .finally(() => setModelLoaded(true));
  }, [agentId, currentApp?.id, agentMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEditMode) return;
    setLoading(true);
    makeRequest(`/v1/apps/${currentApp.id}/agents/${agentId}`, {
      method: 'GET',
      authTokens: tokens,
    })
      .then(data => {
        const agentObj = data?.agent || (data?.id ? data : null);
        if (agentObj) {
          const a = agentObj;
          const loaded = {
            name: a.name || '',
            logo_url: a.logo || a.metadata?.logo_url || '',
            agent_type: a.agent_type || '',
            agent_workflow_type: a.agent_workflow_type || a.metadata?.agent_workflow_type || '',
            business_unit_id: a.business_unit_id || '',
            metadata: {
              timezone: a.metadata?.timezone || 'Eastern Time (ET)',
              language: a.metadata?.language || 'English',
            },
          };
          originalDataRef.current = loaded;
          setAgentData(loaded);
          setAgentMode(a.agent_mode || null);
          setSiteCode(a.site_code || '');
          dispatch(setSelectedAgentName(a.name));
          dispatch(setSelectedAgentMode(a.agent_mode || null));
          setPageMeta({
            agentName: a.name,
            description: "Configure your agent's basic information and define its role.",
            actions: a.agent_mode === 'legacy' ? null : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Test Agent" placement="bottom" arrow>
                  <Box
                    onClick={() => navigate(`/${currentApp.url_code}/agent/${agentId}/settings/bot-testing`)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      px: '12px', height: 38, borderRadius: '10px',
                      border: `1.5px solid ${SLATE[300]}`, bgcolor: '#fff',
                      cursor: 'pointer', overflow: 'hidden',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: SLATE[50] },
                    }}
                  >
                    <ScienceOutlinedIcon sx={{ fontSize: '1.1rem', color: SLATE[600], flexShrink: 0 }} />
                    <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[800], lineHeight: 1 }}>
                      Test Agent
                    </Typography>
                  </Box>
                </Tooltip>
                <Tooltip title="View Changes" placement="bottom" arrow>
                  <Box
                    onClick={() => setDiffModalOpen(true)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: '5px',
                      px: '12px', height: 38, borderRadius: '10px',
                      border: `1.5px solid ${SLATE[300]}`, bgcolor: '#fff',
                      cursor: 'pointer', overflow: 'hidden',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: SLATE[50] },
                    }}
                  >
                    <CompareArrowsOutlinedIcon sx={{ fontSize: '1.1rem', color: SLATE[600], flexShrink: 0 }} />
                    <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[800], lineHeight: 1 }}>
                      View Changes
                    </Typography>
                  </Box>
                </Tooltip>
                {!['stage', 'production'].includes(process.env.REACT_APP_CURRENT_ENVIRONMENT) && ( // eslint-disable-line no-undef
                  <>
                    <Tooltip title="Push to Dev" placement="bottom" arrow>
                      <Box
                        onClick={() => setPushDevDialogOpen(true)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: '5px',
                          px: '12px', height: 38, borderRadius: '10px',
                          border: `1.5px solid ${SLATE[300]}`, bgcolor: '#fff',
                          cursor: 'pointer', overflow: 'hidden',
                          transition: 'background 0.15s',
                          '&:hover': { bgcolor: SLATE[50] },
                        }}
                      >
                        <RocketLaunchOutlinedIcon sx={{ fontSize: '1.1rem', color: SLATE[600], flexShrink: 0 }} />
                        <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[800], lineHeight: 1 }}>
                          Push to Dev
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Tooltip title="✨ Configure with AI" placement="bottom" arrow>
                      <Box
                        onClick={() => navigate(`/${currentApp.url_code}/agent/${agentId}/chat`)}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: '5px',
                          px: '12px', height: 38, borderRadius: '10px',
                          border: `1.5px solid ${SLATE[300]}`, bgcolor: '#fff',
                          cursor: 'pointer', overflow: 'hidden',
                          transition: 'background 0.15s',
                          '&:hover': { bgcolor: SLATE[50] },
                        }}
                      >
                        <Typography sx={{ fontSize: '0.9rem', lineHeight: 1, flexShrink: 0 }}>✨</Typography>
                        <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: SLATE[800], lineHeight: 1 }}>
                          Configure with AI
                        </Typography>
                      </Box>
                    </Tooltip>
                  </>
                )}
              </Box>
            ),
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (key, value) => {
    setAgentData(prev => ({ ...prev, [key]: value }));
  };

  const handleMetaChange = (key, value) => {
    setAgentData(prev => ({ ...prev, metadata: { ...prev.metadata, [key]: value } }));
  };

  const handleLogoChange = e => {
    handleChange('logo_url', e.target.value[0]);
  };

  const handleSave = async () => {
    if (isEditMode && modelLoaded) {
      const errors = {
        default: modelData.default ? '' : 'Default model is required',
        fallback: modelData.fallback ? '' : 'Fallback model is required',
      };
      if (errors.default || errors.fallback) {
        setModelErrors(errors);
        return;
      }
    }
    setSaving(true);
    const isAgentDirty = JSON.stringify(agentData) !== JSON.stringify(originalDataRef.current);
    const metadata = { ...agentData.metadata, logo_url: agentData.logo_url };
    try {
      if (isEditMode) {
        if (isAgentDirty) {
          const response = await makeRequest(`/v1/apps/${currentApp.id}/agents/${agentId}`, {
            method: 'PUT',
            body: {
              agent: {
                name: agentData.name,
                agent_type: agentData.agent_type,
                agent_workflow_type: agentData.agent_workflow_type,
                business_unit_id: agentData.business_unit_id || null,
                metadata,
              },
            },
            authTokens: tokens,
          });
          if (!response) {
            setSaving(false);
            return;
          }
        }
        if (isModelDirty) {
          const modelBody = {};
          if (modelData.default) modelBody.default = modelData.default;
          if (modelData.fallback) modelBody.fallback = modelData.fallback;
          if (Object.keys(modelBody).length > 0) {
            await makeRequest(`/v1/apps/${currentApp.id}/agents/${agentId}/model`, {
              method: 'PUT',
              body: modelBody,
              authTokens: tokens,
            });
            originalModelRef.current = { ...modelData };
          }
        }
        if (isAgentDirty || isModelDirty) {
          success(TOAST.agent.saved);
        }
        navigate(`/${currentApp.url_code}/agent/${agentId}/profile`);
      } else {
        const response = await makeRequest(`/v1/apps/${currentApp.id}/agents`, {
          method: 'POST',
          body: {
            agent: {
              name: agentData.name,
              agent_type: agentData.agent_type,
              agent_workflow_type: agentData.agent_workflow_type || 'llm_agent',
              business_unit_id: agentData.business_unit_id || undefined,
              status: 'active',
              ...(isLegacyCreate && { agent_mode: 'legacy' }),
              metadata,
            },
          },
          authTokens: tokens,
        });
        if (response?.agent) {
          success(TOAST.agent.created);
          if (response.agent.site_code)
            dispatch(setSelectedAgentSiteCode(response.agent.site_code));
          dispatch(setSelectedAgentName(response.agent.name));
          dispatch(setSelectedAgentMode(response.agent.agent_mode || null));
          navigate(`/${currentApp.url_code}/agent/${response.agent.id}/profile`);
        }
      }
    } catch (_err) {
      // error already toasted by useApiCall
    } finally {
      setSaving(false);
    }
  };

  const handlePushToDev = async () => {
    setPushingDev(true);
    setPushDevResult(null);
    try {
      const response = await makeRequest(
        `/v1/apps/${currentApp.id}/agents/${agentId}/push_agent_dev`,
        { method: 'POST', authTokens: tokens, ignore_error: true }
      );
      setPushDevResult(response || {});
    } catch (_err) {
      setPushDevResult({});
    } finally {
      setPushingDev(false);
    }
  };

  const closePushDevDialog = () => {
    setPushDevDialogOpen(false);
    setPushDevResult(null);
  };

  if (loading) {
    return <LoadingSpinner message="Loading agent profile..." />;
  }

  return (
    <>
    <Box
      component="form"
      onSubmit={e => {
        e.preventDefault();
        handleSave();
      }}
      sx={{ width: '100%', maxWidth: 720, pb: 10 }}
    >
      {/* Basic Information */}
      <Box
        sx={{
          bgcolor: '#ffffff',
          border: `1px solid ${SLATE[200]}`,
          borderRadius: '16px',
          p: 3,
          mb: 2.5,
        }}
      >
        <SectionHeader
          title="Basic Information"
          description="Set the agent's name and visual identity."
        />

        {/* Agent Name */}
        <Box sx={{ mb: 2.5 }}>
          <Typography component="label" sx={FIELD_LABEL_SX}>
            Agent Name{' '}
            <Box component="span" sx={{ color: '#ef4444' }}>
              *
            </Box>
          </Typography>
          <TrimTextField
            fullWidth
            size="small"
            required
            placeholder="e.g. Enrollment Agent"
            value={agentData.name}
            onChange={e => handleChange('name', e.target.value)}
            sx={INPUT_SX}
            inputProps={{
              onInvalid: e => e.target.setCustomValidity('Please enter an agent name'),
              onInput: e => e.target.setCustomValidity(''),
            }}
          />
        </Box>

        {/* Internal Agent Name (site_code) — read-only, edit mode only */}
        {isEditMode && siteCode && (
          <Box sx={{ mb: 2.5 }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Internal Agent Name{' '}
              <Tooltip
                title="The agent's internal identifier. Generated from the agent name when the agent is created, and used by deployments and configuration files. It does not change when you rename the agent."
                placement="top"
                arrow
              >
                <InfoOutlinedIcon
                  sx={{ fontSize: '0.875rem', color: SLATE[400], verticalAlign: 'text-bottom' }}
                />
              </Tooltip>
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={siteCode}
              InputProps={{ readOnly: true }}
              sx={{
                ...INPUT_SX,
                '& .MuiOutlinedInput-root': {
                  ...INPUT_SX['& .MuiOutlinedInput-root'],
                  bgcolor: SLATE[50],
                },
                '& .MuiInputBase-input': {
                  color: SLATE[700],
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: '0.8125rem',
                },
              }}
            />
          </Box>
        )}

        {/* Agent Logo */}
        <Box>
          <Typography component="label" sx={FIELD_LABEL_SX}>
            Agent Logo / Icon
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '12px',
                border: `1px solid ${SLATE[200]}`,
                bgcolor: SLATE[50],
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                flexShrink: 0,
              }}
            >
              {agentData.logo_url ? (
                <img
                  src={encodeURI(agentData.logo_url)}
                  alt="logo"
                  style={{ width: 48, height: 48, objectFit: 'contain' }}
                />
              ) : (
                <Typography sx={{ fontSize: '0.6875rem', color: SLATE[400] }}>No logo</Typography>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <TrimTextField
                fullWidth
                size="small"
                placeholder="https://example.com/logo.png"
                value={agentData.logo_url}
                onChange={e => handleChange('logo_url', e.target.value)}
                sx={{ ...INPUT_SX, mb: 1 }}
              />
              <FileUpload
                field={{
                  name: 'fileUpload',
                  title: agentData.logo_url ? 'Change Logo' : 'Upload Logo',
                  multiple: false,
                }}
                accept={ACCEPTED_LOGO_FORMATS}
                endpoint="/v1/apps/logo_upload"
                onChange={handleLogoChange}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Configuration */}
      <Box
        sx={{
          bgcolor: '#ffffff',
          border: `1px solid ${SLATE[200]}`,
          borderRadius: '16px',
          p: 3,
          mb: 2.5,
        }}
      >
        <SectionHeader
          title="Configuration"
          description="Set the agent's language, timezone, and functional type."
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
          {/* Timezone */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Timezone{' '}
              <Box component="span" sx={{ color: '#ef4444' }}>
                *
              </Box>
            </Typography>
            <Autocomplete
              size="small"
              options={TIMEZONES}
              value={TIMEZONES.find(t => t.value === agentData.metadata?.timezone) || TIMEZONES[3]}
              getOptionLabel={option => option.label}
              disableClearable
              onChange={(_e, val) =>
                handleMetaChange('timezone', val?.value || 'Eastern Time (ET)')
              }
              renderInput={params => <TextField {...params} required sx={INPUT_SX} />}
            />
          </Box>

          {/* Primary Language */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Primary Language{' '}
              <Box component="span" sx={{ color: '#ef4444' }}>
                *
              </Box>
            </Typography>
            <Autocomplete
              size="small"
              options={LANGUAGES}
              value={LANGUAGES.find(l => l.value === agentData.metadata?.language) || LANGUAGES[0]}
              getOptionLabel={option => option.label}
              disableClearable
              onChange={(_e, val) => handleMetaChange('language', val?.value || 'English')}
              renderInput={params => <TextField {...params} required sx={INPUT_SX} />}
            />
          </Box>

          {/* Agent Workflow Type */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Agent Workflow Type{' '}
              <Box component="span" sx={{ color: '#ef4444' }}>
                *
              </Box>
            </Typography>
            <Autocomplete
              size="small"
              options={AGENT_WORKFLOW_TYPES}
              value={
                AGENT_WORKFLOW_TYPES.find(t => t.value === agentData.agent_workflow_type) || null
              }
              getOptionLabel={option => option.label}
              onChange={(_e, val) => {
                handleChange('agent_workflow_type', val?.value || '');
                if (workflowTypeInputRef.current)
                  workflowTypeInputRef.current.setCustomValidity('');
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  placeholder="Select workflow type..."
                  sx={INPUT_SX}
                  inputRef={workflowTypeInputRef}
                  inputProps={{
                    ...params.inputProps,
                    onInvalid: e => e.target.setCustomValidity('Please select a workflow type'),
                  }}
                />
              )}
            />
          </Box>

          {/* Business Unit */}
          <Box>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Business Unit{' '}
              <Box component="span" sx={{ color: '#ef4444' }}>
                *
              </Box>
            </Typography>
            <Autocomplete
              size="small"
              options={businessUnits}
              value={businessUnits.find(bu => bu.id === agentData.business_unit_id) || null}
              getOptionLabel={option => option.name}
              onChange={(_e, val) => {
                handleChange('business_unit_id', val?.id || '');
                if (businessUnitInputRef.current)
                  businessUnitInputRef.current.setCustomValidity('');
              }}
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  placeholder="Select business unit..."
                  sx={INPUT_SX}
                  inputRef={businessUnitInputRef}
                  inputProps={{
                    ...params.inputProps,
                    onInvalid: e => e.target.setCustomValidity('Please select a business unit'),
                  }}
                />
              )}
            />
          </Box>

          {/* Agent Type */}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography component="label" sx={FIELD_LABEL_SX}>
              Agent Type{' '}
              <Box component="span" sx={{ color: '#ef4444' }}>
                *
              </Box>
              <Typography component="span" sx={{ fontSize: '0.75rem', color: SLATE[400], ml: 1 }}>
                (You can change this if needed)
              </Typography>
            </Typography>
            <Autocomplete
              size="small"
              options={AGENT_PROFILE_TYPES}
              value={AGENT_PROFILE_TYPES.find(t => t.value === agentData.agent_type) || null}
              getOptionLabel={option => `${option.emoji} ${option.label}`}
              onChange={(_e, val) => {
                handleChange('agent_type', val?.value || '');
                if (agentTypeInputRef.current) agentTypeInputRef.current.setCustomValidity('');
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.5 }}>
                    <Typography sx={{ fontSize: '1.1rem', lineHeight: 1.4 }}>
                      {option.emoji}
                    </Typography>
                    <Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: SLATE[900] }}>
                        {option.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: SLATE[500] }}>
                        {option.description}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  required
                  placeholder="Select an agent type..."
                  sx={INPUT_SX}
                  inputRef={agentTypeInputRef}
                  inputProps={{
                    ...params.inputProps,
                    onInvalid: e => e.target.setCustomValidity('Please select an agent type'),
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </Box>

      {/* Advanced Settings — model selection, edit mode only */}
      {isEditMode && agentMode === 'agent' && modelLoaded && (
        <Box
          sx={{
            bgcolor: '#ffffff',
            border: `1px solid ${SLATE[200]}`,
            borderRadius: '16px',
            p: 3,
            mb: 2.5,
          }}
        >
          <SectionHeader
            title="Advanced Settings"
            description="Configure the AI model used by this agent."
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>
                Default Model{' '}
                <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
              </Typography>
              <Autocomplete
                size="small"
                options={availableModels}
                value={modelData.default || null}
                disableClearable={false}
                onChange={(_e, val) => {
                  setModelData(m => ({ ...m, default: val || '' }));
                  setModelErrors(e => ({ ...e, default: '' }));
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Select default model..."
                    error={Boolean(modelErrors.default)}
                    helperText={modelErrors.default || undefined}
                    sx={INPUT_SX}
                  />
                )}
              />
            </Box>
            <Box>
              <Typography component="label" sx={FIELD_LABEL_SX}>
                Fallback Model{' '}
                <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
              </Typography>
              <Autocomplete
                size="small"
                options={availableModels}
                value={modelData.fallback || null}
                disableClearable={false}
                onChange={(_e, val) => {
                  setModelData(m => ({ ...m, fallback: val || '' }));
                  setModelErrors(e => ({ ...e, fallback: '' }));
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder="Select fallback model..."
                    error={Boolean(modelErrors.fallback)}
                    helperText={modelErrors.fallback || undefined}
                    sx={INPUT_SX}
                  />
                )}
              />
            </Box>
          </Box>
        </Box>
      )}

      <BottomActionBar
        name={isEditMode ? 'Save Agent' : 'Create Agent'}
        handleCancel={() => navigate(`/${currentApp?.url_code}/`)}
        saving={saving}
        isDirty={isDirty}
      />
    </Box>

    <Dialog
      open={pushDevDialogOpen}
      onClose={() => !pushingDev && closePushDevDialog()}
      maxWidth="xs"
      fullWidth
    >
      {pushDevResult ? (
        (() => {
          const overall = getPushDevOverall(pushDevResult.steps);
          return (
            <>
              <DialogContent sx={{ pt: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    mb: 2.5,
                  }}
                >
                  {PUSH_DEV_TONE_ICON[overall.tone]}
                  <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900], mt: 1 }}>
                    {overall.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8125rem', color: SLATE[600], mt: 0.5 }}>
                    {overall.description}
                  </Typography>
                </Box>

                {pushDevResult.steps && (
                  <Box sx={{ borderTop: `1px solid ${SLATE[200]}`, pt: 1.5 }}>
                    {PUSH_DEV_STEP_KEYS.map(key => (
                      <PushDevStepRow
                        key={key}
                        stepKey={key}
                        bucket={getPushDevBucket(pushDevResult.steps, key)}
                      />
                    ))}
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={() => { closePushDevDialog(); setDiffModalOpen(true); }}
                  sx={{
                    textTransform: 'none',
                    borderColor: SLATE[300],
                    color: SLATE[700],
                    '&:hover': { borderColor: SLATE[400], bgcolor: SLATE[50] },
                  }}
                  startIcon={<CompareArrowsOutlinedIcon sx={{ fontSize: '1rem' }} />}
                >
                  View Changes
                </Button>
                <Button
                  variant="contained"
                  onClick={closePushDevDialog}
                  sx={{ textTransform: 'none', boxShadow: 'none', flex: 1, '&:hover': { boxShadow: 'none' } }}
                >
                  Close
                </Button>
              </DialogActions>
            </>
          );
        })()
      ) : pushingDev ? (
        <DialogContent sx={{ py: 5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={28} />
            <Typography sx={{ fontSize: '0.875rem', color: SLATE[600] }}>
              Pushing your agent to Dev...
            </Typography>
          </Box>
        </DialogContent>
      ) : (
        <>
          <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600, color: SLATE[900] }}>
            Push to Dev?
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: '0.875rem', color: SLATE[600] }}>
              This will push the current agent to Dev. Do you want to continue?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              variant="outlined"
              onClick={closePushDevDialog}
              sx={{ textTransform: 'none', borderColor: SLATE[300], color: SLATE[700] }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePushToDev}
              sx={{ textTransform: 'none', boxShadow: 'none', '&:hover': { boxShadow: 'none' } }}
            >
              Push
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>

    <DiffViewerModal
      open={diffModalOpen}
      onClose={() => setDiffModalOpen(false)}
      appId={currentApp?.id}
      agentId={agentId}
    />
    </>
  );
}
